// Helpers for the "Topsail Representative Initials" field in the proposal / change
// order templates.
//
// The rep initials appear in ~9 places across a signed document. We save a single
// string on the proposal/change order (topsailRepInitials) and interpolate it into
// the template. If nothing is saved yet, we fall back to initials derived from the
// createdBy user's firstName + lastName.

// Derive initials from a first name + last name (e.g. "John", "Smith" -> "JS").
export const initialsFromName = (firstName = '', lastName = '') => {
  const first = String(firstName || '').trim();
  const last = String(lastName || '').trim();
  const firstInitial = first ? first[0].toUpperCase() : '';
  const lastInitial = last ? last[0].toUpperCase() : '';
  return `${firstInitial}${lastInitial}`;
};

// Derive initials from a single "full name" string (e.g. "John A Smith" -> "JS").
export const initialsFromFullName = (fullName = '') => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return `${parts[0][0].toUpperCase()}${parts[parts.length - 1][0].toUpperCase()}`;
};

// Resolve the rep initials to display in the template. Preference order:
//   1. Whatever the user explicitly set on the proposal/change order.
//   2. Initials derived from the createdBy (or provided fallback) user.
//   3. Empty string.
//
// Handles both the User shape returned by the server (nested `user.profile.firstName`)
// and the client Redux `state.auth.user` shape (flat `user.firstName`).
export const resolveRepInitials = (savedValue, user) => {
  if (savedValue && String(savedValue).trim()) {
    return String(savedValue).trim();
  }
  if (user) {
    const firstName = user.firstName || user.profile?.firstName || '';
    const lastName = user.lastName || user.profile?.lastName || '';
    if (firstName || lastName) {
      return initialsFromName(firstName, lastName);
    }
    if (user.name) {
      return initialsFromFullName(user.name);
    }
    if (user.email) {
      return String(user.email)[0].toUpperCase();
    }
  }
  return '';
};

// Wire up inline-editing on all `[data-rep-initials]` slots inside `container`.
//
// The template renders the Topsail Rep Initials in ~9 places. To let a rep edit
// them directly in the preview, we mark every slot as `contenteditable`, give
// it a subtle highlight, and:
//   - on `input`, mirror the typed value into the OTHER slots so all pages stay
//     in sync while the user is typing (without touching the currently-focused
//     slot's DOM, which would clobber the caret);
//   - on `blur`, call `onCommit(finalValue)` so the caller can persist the value
//     into React state / the backend.
//
// Returns a cleanup function that removes the listeners.
export const attachInlineRepInitialsEditor = (container, { onCommit } = {}) => {
  if (!container || typeof container.querySelectorAll !== 'function') {
    return () => {};
  }
  const slots = Array.from(container.querySelectorAll('[data-rep-initials]'));
  if (slots.length === 0) return () => {};

  const handlers = [];
  slots.forEach(slot => {
    slot.setAttribute('contenteditable', 'true');
    slot.setAttribute('spellcheck', 'false');
    slot.setAttribute('data-rep-initials-editable', 'true');
    slot.style.minWidth = '2ch';
    slot.style.display = 'inline-block';
    slot.style.padding = '0 4px';
    slot.style.background = 'rgba(59, 130, 246, 0.08)';
    slot.style.borderRadius = '2px';
    slot.style.outline = 'none';
    slot.style.cursor = 'text';
    slot.title = 'Click to edit Topsail rep initials';

    const onInput = (event) => {
      const raw = (event.target.textContent || '').replace(/\s+/g, '').toUpperCase();
      slots.forEach(other => {
        if (other === event.target) return;
        if ((other.textContent || '') !== raw) {
          other.textContent = raw;
        }
      });
    };

    const onBlur = (event) => {
      const finalValue = (event.target.textContent || '').replace(/\s+/g, '').toUpperCase();
      if (finalValue !== event.target.textContent) {
        slots.forEach(s => { s.textContent = finalValue; });
      }
      if (typeof onCommit === 'function') {
        onCommit(finalValue);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.target.blur();
      }
    };

    slot.addEventListener('input', onInput);
    slot.addEventListener('blur', onBlur);
    slot.addEventListener('keydown', onKeyDown);
    handlers.push({ slot, onInput, onBlur, onKeyDown });
  });

  return () => {
    handlers.forEach(({ slot, onInput, onBlur, onKeyDown }) => {
      slot.removeEventListener('input', onInput);
      slot.removeEventListener('blur', onBlur);
      slot.removeEventListener('keydown', onKeyDown);
      slot.removeAttribute('contenteditable');
      slot.removeAttribute('data-rep-initials-editable');
    });
  };
};
